import { BasicItemsResponse, BasicResponse } from "../../../responses/basicResponses";
import { ICustomPanelSource } from "../../../responses/customPanelSource";
import { ICustomPanelType } from "../../../responses/customPanelTypes";
import { IPanelSource } from "../../../responses/panelSource";
import { ExtrusionGeometry } from "../geometry/claddingCellExtrusionGeometryFactory";
import { ModelBufferedGeometry } from "../geometry/modelBufferedGeometry";
import { TextGeometryFactory } from "../geometry/textGeometryFactory";
import { getPanelUserUniqueIdObjectPrefix } from "../ids/userObjectIds";
import { Panel } from "../panels/panel";
import { PanelFacadeDocument } from "../panels/panelFacadeDocument";
import { PanelFamily } from "../panels/panelFamily";
import { PanelParameters } from "../panels/panelParameters";
import { PanelType } from "../panels/panelType";
import { toBasicItemsResponse } from "../utils/toBasicItemsResponse";
import { updateAllTypes } from "../utils/updateAllTypes";
import type { IPanelLouvreCutouts, IPanelType, IPanelWindowDesignParameters } from "../../../responses/panelType";
import repo from "../../../Repository";

export class PanelsModelEditor {
    private readonly panelsTitlesFragments = new Map<number, number>();
    private readonly panelsCladdingCellsFragments = new Map<number, number[]>();

    constructor(
        private readonly panelFacadeDocument: PanelFacadeDocument,
        private readonly modelBufferedGeometry: ModelBufferedGeometry,
        private readonly textGeometryFactory: TextGeometryFactory,
    ) {}

    get modelId(): number {
        return this.modelBufferedGeometry.model.id;
    }

    get model(): Autodesk.Viewing.Model {
        return this.modelBufferedGeometry.model;
    }

    get panels(): Panel[] {
        return this.panelFacadeDocument.panels;
    }

    get facadeModelId(): string {
        return this.panelFacadeDocument.modelId;
    }

    get panelTypes(): PanelType<IPanelSource>[] {
        return this.panelFacadeDocument.panelTypes;
    }

    get convertedPanelTypes(): PanelType<IPanelSource>[] {
        return this.panelFacadeDocument.convertedPanelTypes;
    }

    createPanels() {
        for (const panel of this.panelFacadeDocument.panels) {
            this.addPanel(panel);
        }

        this.modelBufferedGeometry.makeFixOnObjectRecreation();
    }

    findPanel(dbId: number): Panel | undefined {
        return this.panelFacadeDocument.findPanel(dbId);
    }

    findPanelById(id: string): Panel | undefined {
        return this.panelFacadeDocument.findPanelById(id);
    }

    findPanelsByTypeId(typeId: string): Panel[] {
        return this.panelFacadeDocument.findPanelsByTypeId(typeId);
    }

    findPanelFamily(panelTypeId: string): PanelFamily<IPanelSource> | undefined {
        return this.panelFacadeDocument.findPanelFamily(panelTypeId);
    }

    async update({
        panelId,
        elementName,
        installationSequence,
    }: {
        panelId: string;
    } & PanelParameters): Promise<BasicResponse> {
        const panel = this.findPanelById(panelId);

        if (!panel) {
            return { isSuccess: false, message: `Failed to find panel with id=${panelId}` };
        }
        const initialInstallationSequence = panel.installationSequence;

        if (installationSequence) {
            const facadeElement =
                this.panelFacadeDocument.findFacadeElementByInstallationSequence(installationSequence);

            if (facadeElement && facadeElement.id !== panel.id) {
                return {
                    isSuccess: false,
                    message: `Failed to update panel "${panel.elementName || getPanelUserUniqueIdObjectPrefix() + panel.userUniqueId
                        }", installation sequence already exists in element "${facadeElement.elementName || getPanelUserUniqueIdObjectPrefix() + facadeElement.userUniqueId
                        }"`,
                };
            }
        }

        const response = await repo.updatePanel(panelId, {
            elementName,
            installationSequence,
        });

        if (response.isSuccess) {
            panel.update({ elementName, installationSequence });
            this.panelFacadeDocument.updateInstallationSequence(
                initialInstallationSequence,
                installationSequence,
                panel,
            );
        }

        return response;
    }

    findPanelByUserId(userUniqueId: number): Panel | undefined {
        return this.panelFacadeDocument.findPanelByUserId(userUniqueId);
    }

    findPanelByTypeName(typeName: string): Panel | undefined {
        return this.panelFacadeDocument.findPanelByTypeName(typeName);
    }

    findPanelByCustomTypeName(typeName: string): Panel | undefined {
        return this.panelFacadeDocument.findPanelByCustomTypeName(typeName);
    }

    findPanelByElementName(elementName: string): Panel | undefined {
        return this.panelFacadeDocument.findPanelByElementName(elementName);
    }

    findValidCustomPanelTypesForPanel(panelId: string): ICustomPanelType[] {
        return this.panelFacadeDocument.findValidCustomPanelTypesForPanel(panelId);
    }

    findPanelCustomPanelType(panelId: string): PanelType<ICustomPanelSource> | undefined {
        const panel = this.findPanelById(panelId);

        if (!panel?.customPanelTypeId) {
            return undefined;
        }

        return this.panelFacadeDocument.findCustomPanelType(panel.customPanelTypeId);
    }

    findConvertedPanelType(customPanelTypeId: string): PanelType<IPanelSource> | undefined {
        return this.panelFacadeDocument.findConvertedPanelType(customPanelTypeId);
    }

    findPanelCustomTypeSource(panelId: string): ICustomPanelType | undefined {
        const panel = this.findPanelById(panelId);

        if (!panel?.customPanelTypeId) {
            return undefined;
        }

        return this.panelFacadeDocument.findCustomPanelTypeSource(panel.customPanelTypeId);
    }

    findPanelType(panelTypeId: string): PanelType<IPanelSource> | undefined {
        return this.panelFacadeDocument.findPanelType(panelTypeId);
    }

    updatePanelTypeTexts(panelId: string) {
        const panel = this.panelFacadeDocument.findPanelById(panelId);
        if (!panel) {
            throw new Error("Invalid state!");
        }

        const panelType = this.panelFacadeDocument.findPanelType(panel.panelTypeId);
        if (!panelType) {
            throw new Error("Invalid state!");
        }

        for (const panel of panelType.panels) {
            this.addPanelTypeText(panel);
            this.recreatePanelGeometry(panel);
        }

        this.modelBufferedGeometry.makeFixOnObjectRecreation();
    }
    
    async updatePanelType(
        panelTypeProperties: Omit<IPanelType, "hooks" | "windowsDesignParameters" | "louvreCutouts">,
        skipGraphicsUpdate = false,
    ): Promise<BasicResponse> {
        const panelType = this.panelFacadeDocument.findPanelType(panelTypeProperties.id);

        if (!panelType) {
            return { isSuccess: false, message: `Failed to find panel type with id=${panelTypeProperties.id}` };
        }

        const result = await repo.updatePanelType(panelType.id, {
            typeName: panelTypeProperties.name,
            withoutCladdings: panelTypeProperties.withoutCladdings,
        });

        if (result.isSuccess) {
            panelType.name = panelTypeProperties.name;
            panelType.withoutCladdings = panelTypeProperties.withoutCladdings;

            for (const panel of panelType.panels) {
                if (!skipGraphicsUpdate) {
                    this.addPanelTypeText(panel);
                    this.recreatePanelGeometry(panel);
                }
            }

            this.modelBufferedGeometry.makeFixOnObjectRecreation();
        }

        return result;
    }

    async setPanelTypeWindowDesignParameters(
        panelTypeId: string,
        windowIndex: number,
        parameters: IPanelWindowDesignParameters,
    ): Promise<BasicResponse> {
        const panelType = this.panelFacadeDocument.findPanelType(panelTypeId);
        if (!panelType) {
            return { isSuccess: false, message: `Failed to find panel type with id=${panelTypeId}` };
        }

        const result = await repo.updateWindowDesignParameters(panelTypeId, { ...parameters, windowIndex });

        if (result.isSuccess) {
            panelType.setPanelWindowDesignParameters(windowIndex, parameters);
        }

        return result;
    }

    async setPanelTypeLouvreCutouts(
        panelTypeId: string,
        louvreIndex: number,
        cutouts: IPanelLouvreCutouts,
    ): Promise<BasicResponse> {
        const panelType = this.panelFacadeDocument.findPanelType(panelTypeId);
        if (!panelType) {
            return { isSuccess: false, message: `Failed to find panel type with id=${panelTypeId}` };
        }

        const result = await repo.updateLouvreCutouts(panelTypeId, { ...cutouts, louvreIndex });

        if (result.isSuccess) {
            panelType.setLouvreCutouts(louvreIndex, cutouts);
        }

        return result;
    }

    async switchPanelType(panelId: string, targetTypeId: string): Promise<CreatePanelTypeOperationResult> {
        const switchPanelTypeResult = await repo.setPanelType(panelId, { targetTypeId });

        if (!switchPanelTypeResult.isSuccess) {
            return { isSuccess: false, removedHooksDbIds: [], message: switchPanelTypeResult.message };
        }

        const removedHooksDbIds = this.panelFacadeDocument.setPanelType(panelId, targetTypeId);

        return { isSuccess: true, removedHooksDbIds, message: null };
    }

    async createPanelType(
        panelType: Omit<IPanelType, "hooks" | "windowsDesignParameters" | "louvreCutouts">,
        panelId: string,
    ): Promise<CreatePanelTypeOperationResult> {
        const { id: panelTypeId, name: typeName, withoutCladdings } = panelType;
        const family = this.panelFacadeDocument.findPanelFamily(panelTypeId);

        if (!family) {
            throw new Error("Invalid state!");
        }

        const panelTypeCreationResult = await repo.duplicatePanelType(family.id, {
            sourcePanelTypeId: panelTypeId,
            typeName,
            withoutCladdings,
        });

        if (!panelTypeCreationResult.isSuccess) {
            return { isSuccess: false, removedHooksDbIds: [], message: panelTypeCreationResult.message };
        }

        const typeId = panelTypeCreationResult.item;

        this.panelFacadeDocument.addPanelType(typeId, panelTypeId, typeName, withoutCladdings);

        return await this.switchPanelType(panelId, typeId);
    }

    async switchPanelCustomType(
        panelId: string,
        targetCustomPanelTypeId: string | null,
    ): Promise<CreatePanelTypeOperationResult> {
        const panel = this.panelFacadeDocument.findPanelById(panelId);

        if (!panel || panel.customPanelTypeId === targetCustomPanelTypeId) {
            return { isSuccess: true, removedHooksDbIds: [], message: null };
        }

        const result = targetCustomPanelTypeId !== null
            ? await repo.setPanelCustomType(panelId, { targetTypeId: targetCustomPanelTypeId })
            : await repo.resetPanelCustomType(panelId);

        if (!result.isSuccess) {
            return { isSuccess: false, removedHooksDbIds: [], message: result.message };
        }

        this.setPanelCustomType(panel, targetCustomPanelTypeId);

        const removedHooksDbIds = targetCustomPanelTypeId
            ? this.panelFacadeDocument.findPanelHooks(panel.id).map(x => x.dbId)
            : [];

        return { isSuccess: true, removedHooksDbIds, message: null };
    }

    async batchUpdateTypes(
        withoutCladdings: boolean,
        ids?: string[],
    ): Promise<BasicItemsResponse<string>> {
        const types = ids
            ? ids.map(x => this.panelFacadeDocument.findPanelType(x)).filter((x): x is PanelType<IPanelSource> => !!x)
            : this.panelFacadeDocument.panelTypes;
    
        const changedIds: string[] = [];
        const result = await updateAllTypes(
            types,
            async x => {
                if (x.withoutCladdings !== withoutCladdings) {
                    changedIds.push(x.id);
                    return await this.updatePanelType({ id: x.id, name: x.name, withoutCladdings }, true);
                }
                return { isSuccess: true, message: null };
            },
        );
    
        this.renderAll();
        return toBasicItemsResponse(result, changedIds);
    }

    private renderAll(): void {
        const panelTypes = this.panelFacadeDocument.panelTypes;

        for (const panelType of panelTypes) {
            for (const panel of panelType.panels) {
                this.addPanelTypeText(panel);
                this.recreatePanelGeometry(panel);
            }
        }

        this.modelBufferedGeometry.makeFixOnObjectRecreation();
    }

    private addPanel(panel: Panel) {
        const geometries = panel.createGeometry();

        const claddingCellsFragments: number[] = [];

        for (const claddingCellGeometry of geometries.claddingCellsGeometry) {
            const fragmentId = this.addGeometryFragment(claddingCellGeometry, panel.panelDbId);

            claddingCellsFragments.push(fragmentId);
        }

        this.panelsCladdingCellsFragments.set(panel.panelDbId, claddingCellsFragments);

        for (const windowGeometry of geometries.windowsGeometry) {
            this.addGeometryFragment(windowGeometry, panel.panelDbId);
        }

        for (const louvreGeometry of geometries.louvresGeometry) {
            this.addGeometryFragment(louvreGeometry, panel.panelDbId);
        }

        this.addGeometryFragment(geometries.panelBoundaryGeometry, panel.panelDbId);

        this.addPanelTypeText(panel);
    }

    private setPanelCustomType(panel: Panel, targetCustomPanelTypeId: string | null) {
        this.panelFacadeDocument.toggleRegularPanelCustomType(panel.id, targetCustomPanelTypeId);

        this.recreatePanelGeometry(panel);

        this.addPanelTypeText(panel);
    }

    private recreatePanelGeometry(panel: Panel) {
        const claddingCellsGeometries = panel.createGeometry().claddingCellsGeometry;

        const fragments = this.panelsCladdingCellsFragments.get(panel.panelDbId) || [];

        for (let i = 0; i < fragments.length; ++i) {
            this.modelBufferedGeometry.changeFragmentMaterial(fragments[i], claddingCellsGeometries[i].material);
        }
    }

    private addGeometryFragment(geometry: ExtrusionGeometry, dbId: number): number {
        const fragmentId = this.modelBufferedGeometry.add(geometry);

        this.modelBufferedGeometry.changeFragmentsDbId(fragmentId, dbId);

        return fragmentId;
    }

    private addPanelTypeText(panel: Panel) {
        const typeText = this.getPanelTypeText(panel);

        const existingTextFragment = this.panelsTitlesFragments.get(panel.panelDbId);

        if (existingTextFragment) {
            this.modelBufferedGeometry.remove(existingTextFragment);

            this.panelsTitlesFragments.delete(panel.panelDbId);
        }

        if (typeText) {
            const matrix = panel.computePanelTypeTitleMatrix(this.textGeometryFactory.computeSize(typeText));

            const geometry = this.textGeometryFactory.createTextGeometry(typeText, matrix);

            const fragmentId = this.modelBufferedGeometry.add(geometry);

            this.modelBufferedGeometry.changeFragmentsDbId(fragmentId, panel.panelDbId);

            this.panelsTitlesFragments.set(panel.panelDbId, fragmentId);
        }
    }

    private getPanelTypeText(panel: Panel): string {
        if (panel.customPanelTypeId) {
            const customPanelType = this.panelFacadeDocument.findCustomPanelType(panel.customPanelTypeId);

            return customPanelType?.name || "";
        }

        const panelType = this.panelFacadeDocument.findPanelType(panel.panelTypeId);

        return panelType?.name || "";
    }
}

export type CreatePanelTypeOperationResult = BasicResponse & {
    removedHooksDbIds: number[];
};
